import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import KeywordForm from 'talent_search/components/keyword_form';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import { Label } from '@grnhse/seedling/lib/birch/typography';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row.keywords');

type FilterType = 'can_have' | 'must_have' | 'does_not_have';

const KeywordsFiltersGroup = () => {
  const { watch, setValue } = useFormContext();
  const [canHave, mustHave, doesNotHave] = watch([
    'can_have',
    'must_have',
    'does_not_have',
  ]);

  const filtersApplied = [
    canHave.length,
    mustHave.length,
    doesNotHave.length,
  ].filter(Boolean).length;

  const buttonLabel = t('button', { count: filtersApplied });

  const clear = () => {
    setValue('can_have', [], { shouldDirty: true });
    setValue('must_have', [], { shouldDirty: true });
    setValue('does_not_have', [], { shouldDirty: true });
  };

  const handleAdd = (type: FilterType, keywords: string[]) => {
    setValue(type, keywords, { shouldDirty: true });
  };

  const handleRemove = (type: FilterType, keyword: string) => {
    setValue(
      type,
      (watch(type) as string[]).filter((k: string) => k !== keyword),
      { shouldDirty: true }
    );
  };

  return (
    <PopoverButton buttonLabel={buttonLabel} title={t('title')} width="500px">
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Label>{t('can_have')}</Label>
            <HelpTooltip
              label={t('can_have_description')}
              ariaLabel={t('can_have_description')}
            />
          </Stack>

          <KeywordForm
            filterType="can_have"
            values={canHave}
            onAdd={(keywords) => handleAdd('can_have', keywords)}
            onRemove={(keyword) => handleRemove('can_have', keyword)}
            booleanIndicator={t('or')}
            pageName="rediscovery"
          />
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Label>{t('must_have')}</Label>
            <HelpTooltip
              label={t('must_have_description')}
              ariaLabel={t('must_have_description')}
            />
          </Stack>

          <KeywordForm
            filterType="must_have"
            values={mustHave}
            onAdd={(keywords) => handleAdd('must_have', keywords)}
            onRemove={(keyword) => handleRemove('must_have', keyword)}
            booleanIndicator={t('and')}
            pageName="rediscovery"
          />
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Label>{t('does_not_have')}</Label>
            <HelpTooltip
              label={t('does_not_have_description')}
              ariaLabel={t('does_not_have_description')}
            />
          </Stack>

          <KeywordForm
            filterType="does_not_have"
            values={doesNotHave}
            onAdd={(keywords) => handleAdd('does_not_have', keywords)}
            onRemove={(keyword) => handleRemove('does_not_have', keyword)}
            booleanIndicator={t('and')}
            pageName="rediscovery"
          />
        </Stack>

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default KeywordsFiltersGroup;
